import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import * as XLSX from "xlsx";
import "./ResumeOCR.css";
import "./Result.css";
import PrintFormPage from "./PrintFormPage"; // Import the PrintFormPage component

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const PDF_WIDTH = 595;
const PDF_HEIGHT = 842;

const POST_OPTIONS = [
  { hindi: "स्टेनो टाइपिस्ट", english: "Steno Typist" },
  { hindi: "आशुलिपिक", english: "Stenographer" },
  { hindi: "सूचीकार (केटलॉग)", english: "Cataloguer" },
  { hindi: "ओवरसियर", english: "Overseer" },
  { hindi: "प्रयोगशाला सहायक", english: "Laboratory Assistant" },
  { hindi: "लिपिक / कनिष्ठ सहायक", english: "Clerk / Junior Assistant" },
];

const VIBHAG_OPTIONS = [
  { hindi: "मनोविज्ञान विभाग", english: "Psychology Department" },
  { hindi: "सैन्य अध्ययन विभाग", english: "Military Studies Department" },
  { hindi: "गणित विभाग", english: "Mathematics Department" },
  { hindi: "रसायन विज्ञान विभाग", english: "Chemistry Department" },
  { hindi: "भौतिक विज्ञान विभाग", english: "Physics Department" },
  { hindi: "जन्तु विज्ञान विभाग", english: "Zoology Department" },
  { hindi: "वनस्पति विज्ञान विभाग", english: "Botany Department" },
];

const PAYMENT_OPTIONS = ["Bank Draft", "NEFT", "Check", "UPI", "IMPS", "RTGS"];

// ─── Crop helper (outside component so it's never re-created) ─────────────────
function cropPdfCanvas(pdfCanvasEl, crop, displayWidth, displayHeight) {
  const scaleX = pdfCanvasEl.width / displayWidth;
  const scaleY = pdfCanvasEl.height / displayHeight;
  const out = document.createElement("canvas");
  out.width = Math.round(crop.width * scaleX);
  out.height = Math.round(crop.height * scaleY);
  out.getContext("2d").drawImage(
    pdfCanvasEl,
    Math.round(crop.x * scaleX),
    Math.round(crop.y * scaleY),
    out.width,
    out.height,
    0,
    0,
    out.width,
    out.height,
  );
  return out;
}

// ─── Stable helper components (MUST be outside Result to avoid remount) ───────
const InfoRow = ({ label, value }) => (
  <div className="res-info-row">
    <span className="res-label">{label}</span>
    <span className="res-value">{value || "—"}</span>
  </div>
);

const EditInput = ({
  label,
  field,
  placeholder,
  maxLength,
  type = "text",
  form,
  onChange,
}) => (
  <div className="field-group">
    <label>{label}</label>
    <input
      className="field-input"
      type={type}
      placeholder={placeholder}
      value={form[field] || ""}
      maxLength={maxLength}
      onChange={(e) => onChange(field, e.target.value)}
    />
  </div>
);

const EditSelect = ({ label, field, options, emptyLabel, form, onChange }) => (
  <div className="field-group">
    <label>{label}</label>
    <select
      className="field-input"
      value={form[field] || ""}
      onChange={(e) => onChange(field, e.target.value)}
    >
      <option value="">{emptyLabel}</option>
      {options.map((opt, i) =>
        typeof opt === "string" ? (
          <option key={i} value={opt}>
            {opt}
          </option>
        ) : (
          <option key={i} value={opt.hindi}>
            {opt.hindi} ({opt.english})
          </option>
        ),
      )}
    </select>
  </div>
);

// Helper function to extract folder name from PDF bundle
const extractFolderName = (pdfBundle) => {
  return pdfBundle?.replace(".pdf", "") || "";
};

// ─────────────────────────────────────────────────────────────────────────────

const Result = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get navigation state from AdminPanel
  const { clickedPdf, allAssignedPdfs, startRange, endRange } = location.state || {};

  // ── Multi-PDF navigation state ───────────────────────────────────────────
  const [allPersons, setAllPersons] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [personData, setPersonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ── PDF viewer ────────────────────────────────────────────────────────────
  const pdfContainerRef = useRef(null);
  const pdfWrapperRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfScale, setPdfScale] = useState(1);
  const [pdfError, setPdfError] = useState(false);

  // ── Form state ───────────────────────────────────────────────────────────
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [form, setForm] = useState({});
  const [eduRows, setEduRows] = useState([]);
  const [workRows, setWorkRows] = useState([]);
  const [validationErrors, setValidationErrors] = useState({
    mobile: "",
    email: "",
    aadhar: "",
    pinCode: "",
  });

  // ── Crop / Photo / Signature ──────────────────────────────────────────────
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [pdfPageSize, setPdfPageSize] = useState({ width: 0, height: 0 });
  const [photoSignUploading, setPhotoSignUploading] = useState(false);
  const [cropPreview, setCropPreview] = useState(null);
  const [savedPhoto, setSavedPhoto] = useState(null);
  const [savedSignature, setSavedSignature] = useState(null);

  // ── Auth check ───────────────────────────────────────────────────────────
  useEffect(() => {
    const login = sessionStorage.getItem("token");
    if (!login || undefined) {
      alert("Login First");
      navigate("/adminp");
    }
  }, []);

  // ── Fetch all persons data on component load ─────────────────────────────
  useEffect(() => {
    if (startRange && endRange && allAssignedPdfs) {
      fetchAllPersonsData();
    }
  }, [startRange, endRange, allAssignedPdfs]);

  // ── Update UI when currentIndex changes ──────────────────────────────────
  useEffect(() => {
    if (allPersons.length > 0 && currentIndex >= 0 && currentIndex < allPersons.length) {
      const currentPerson = allPersons[currentIndex];
      setPersonData(currentPerson);
      initFormFromData(currentPerson);
      // Reset PDF error when changing person
      setPdfError(false);
      setPageNumber(1);
    }
  }, [currentIndex, allPersons]);

  // ── Helper to extract numeric ID from folder name ────────────────────────
  const extractNumericId = (folderName) => {
    if (!folderName) return Infinity;
    const match = folderName.match(/\d+/);
    return match ? parseInt(match[0], 10) : Infinity;
  };

  // ── Fetch all persons using range API ────────────────────────────────────
  const fetchAllPersonsData = async () => {
    try {
      setLoading(true);
      setError("");
      
      const response = await axios.post(
        `${process.env.REACT_APP_ADMIN_API_URL}/person-by-range-new`,
        {
          start: startRange,
          end: endRange,
          folders: allAssignedPdfs
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`
          }
        }
      );

      if (response.data.success && response.data.data) {
        let personsData = response.data.data;
        
        // Sort by folder ID in increasing order
        personsData = [...personsData].sort((a, b) => {
          const idA = extractNumericId(a.folder);
          const idB = extractNumericId(b.folder);
          return idA - idB;
        });
        
        setAllPersons(personsData);
        
        // Find index of clickedPdf inside allPersons
        const clickedFolderName = extractFolderName(clickedPdf);
        const foundIndex = personsData.findIndex(
          person => String(person.folder).trim() === String(clickedFolderName).trim()
        );
        
        // Set currentIndex to found index (or 0 if not found)
        const initialIndex = foundIndex !== -1 ? foundIndex : 0;
        setCurrentIndex(initialIndex);
        
        // Set initial person data
        if (personsData[initialIndex]) {
          setPersonData(personsData[initialIndex]);
          initFormFromData(personsData[initialIndex]);
        }
      } else {
        setError("No data found for the selected range");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch person data");
    } finally {
      setLoading(false);
    }
  };

  // ── Navigation handlers ──────────────────────────────────────────────────
  const handleNext = () => {
    if (currentIndex < allPersons.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setEditMode(false); // Exit edit mode when navigating
      setSaveError("");
      setSaveSuccess("");
      setValidationErrors({ mobile: "", email: "", aadhar: "", pinCode: "" });
      // Reset crop state
      setCrop(null);
      setCompletedCrop(null);
      setCropPreview(null);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setEditMode(false);
      setSaveError("");
      setSaveSuccess("");
      setValidationErrors({ mobile: "", email: "", aadhar: "", pinCode: "" });
      setCrop(null);
      setCompletedCrop(null);
      setCropPreview(null);
    }
  };

  const handleDropdownChange = (e) => {
    const index = parseInt(e.target.value, 10);
    if (!isNaN(index) && index >= 0 && index < allPersons.length) {
      setCurrentIndex(index);
      setEditMode(false);
      setSaveError("");
      setSaveSuccess("");
      setValidationErrors({ mobile: "", email: "", aadhar: "", pinCode: "" });
      setCrop(null);
      setCompletedCrop(null);
      setCropPreview(null);
    }
  };

  const parseJSON = (val) => {
    if (Array.isArray(val)) return val;
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  };

  const initFormFromData = (data) => {
    if (!data) return;
    
    setForm({
      post: data.post || "",
      vibhag: data.vibhag || "",
      nameEnglish: data.name_english || "",
      nameHindi: data.name_hindi || "",
      fatherName: data.father_name || "",
      dob: data.dob || "",
      gender: data.gender || "",
      category: data.category || "",
      religion: data.religion || "",
      caste: data.caste || "",
      aadharNumber: data.aadhar_number || "",
      mobile: data.mobile || "",
      email: data.email || "",
      address: data.address || "",
      correspondenceAddress:
        data.correspondence_address || data.correspondenceAddress || "",
      pinCode: data.pin_code || "",
      hindiTyping: data.hindi_typing || "",
      englishTyping: data.english_typing || "",
      shorthandSpeed: data.shorthand_speed || "",
      paymentMethod: data.payment_method || "",
      paymentNumber: data.payment_number || "",
      sameAddress: false,
    });

    // Seed saved photo / signature URLs
    setSavedPhoto(data.photo_path ? `${process.env.REACT_APP_FILE_URL}/${data.photo_path}` : null);
    setSavedSignature(
      data.signature_path
        ? `${process.env.REACT_APP_FILE_URL}/${data.signature_path}`
        : null
    );

    const rawEdu = parseJSON(data.edu_rows);
    setEduRows(
      rawEdu.length > 0
        ? rawEdu.map((row, i) => ({
            id: Date.now() + i,
            exam: row.l || row.exam || row.label || "",
            customExam: "",
            board: row.board || "",
            isBoardChecked: row.isBoardChecked || false,
            totalMarks: row.total || row.totalMarks || "",
            obtainedMarks: row.obtain || row.obtainedMarks || "",
            percentage: row.percentage || "",
            scoreType: row.scoreType || "Percentage",
          }))
        : [
            {
              id: Date.now(),
              exam: "",
              customExam: "",
              board: "",
              isBoardChecked: false,
              totalMarks: "",
              obtainedMarks: "",
              percentage: "",
              scoreType: "Percentage",
            },
          ],
    );

    const rawWork = parseJSON(data.work_rows);
    setWorkRows(
      rawWork.length > 0
        ? rawWork.map((row, i) => ({
            id: i + 1,
            orgName: row.o || row.orgName || row.organization || "",
            post: row.p || row.post || "",
            duration: row.d || row.duration || "",
            salary: row.s || row.salary || "",
          }))
        : [{ id: 1, orgName: "", post: "", duration: "", salary: "" }],
    );
  };

  // ── PDF helpers ───────────────────────────────────────────────────────────
  const calcFit = () => {
    if (!pdfContainerRef.current) return;
    const w = pdfContainerRef.current.offsetWidth - 48;
    const h = pdfContainerRef.current.offsetHeight - 32;
    setPdfScale(Math.min(w / PDF_WIDTH, h / PDF_HEIGHT));
  };
  
  const fitWidth = () => {
    if (pdfContainerRef.current)
      setPdfScale((pdfContainerRef.current.offsetWidth - 48) / PDF_WIDTH);
  };
  
  const fitHeight = () => {
    if (pdfContainerRef.current)
      setPdfScale((pdfContainerRef.current.offsetHeight - 32) / PDF_HEIGHT);
  };
  
  const zoomIn = () =>
    setPdfScale((s) => Math.min(+(s + 0.15).toFixed(2), 4));
  
  const zoomOut = () =>
    setPdfScale((s) => Math.max(+(s - 0.15).toFixed(2), 0.2));
  
  const onPdfLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setPdfError(false);
  };
  
  const onPdfLoadError = () => setPdfError(true);

  const onPdfPageRenderSuccess = () => {
    if (!pdfWrapperRef.current) return;
    const canvas = pdfWrapperRef.current.querySelector("canvas");
    if (canvas)
      setPdfPageSize({ width: canvas.offsetWidth, height: canvas.offsetHeight });
  };

  useEffect(() => {
    calcFit();
    window.addEventListener("resize", calcFit);
    return () => window.removeEventListener("resize", calcFit);
  }, []);

  const handleCropChange = (c) => {
    setCrop(c);
  };

  const pdfUrl = personData?.folder
    ? `${process.env.REACT_APP_FILE_URL}/uploads/${personData.folder}.pdf`
    : null;

  const dash = (val) =>
    val !== undefined && val !== null && String(val).trim() !== ""
      ? String(val).trim()
      : "—";

  const safeRows = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  };

  const mappedData = personData
    ? {
        folder: dash(personData.folder),
        post: dash(personData.post),
        vibhag: dash(personData.vibhag),
        nameEnglish: dash(personData.name_english),
        nameHindi: dash(personData.name_hindi),
        fatherName: dash(personData.father_name),
        dob: dash(personData.dob),
        gender: dash(personData.gender),
        category: dash(personData.category),
        religion: dash(personData.religion),
        caste: dash(personData.caste),
        aadharNumber: dash(personData.aadhar_number),
        mobile: dash(personData.mobile),
        email: dash(personData.email),
        address: dash(personData.address),
        correspondenceAddress: dash(
          personData.correspondence_address ?? personData.correspondenceAddress,
        ),
        pinCode: dash(personData.pin_code),
        hindiTyping: dash(personData.hindi_typing),
        englishTyping: dash(personData.english_typing),
        shorthandSpeed: dash(personData.shorthand_speed),
        paymentMethod: dash(personData.payment_method),
        paymentNumber: dash(personData.payment_number),
        workerMobile: dash(personData.worker_mobile),
        createdAt: personData.created_at
          ? new Date(personData.created_at).toLocaleString("en-IN")
          : "—",
        updatedAt: personData.updated_at
          ? new Date(personData.updated_at).toLocaleString("en-IN")
          : "—",
      }
    : null;

  const eduRows_view = safeRows(personData?.edu_rows).map((row, index) => ({
    id: index,
    exam: row.exam || row.l || row.label || "—",
    board: row.board || "—",
    totalMarks: row.totalMarks || row.total || "—",
    obtainedMarks: row.obtainedMarks || row.obtain || "—",
    percentage: row.percentage || "—",
    scoreType: row.scoreType || "Percentage",
    isBoardChecked: row.isBoardChecked || false,
  }));

  const workRows_view = safeRows(personData?.work_rows).map((row, index) => ({
    id: index,
    organization: row.organization || row.orgName || row.o || "—",
    post: row.post || row.p || "—",
    duration: row.duration || row.d || "—",
    salary: row.salary || row.s || "—",
  }));

  // ── Validation ────────────────────────────────────────────────────────────
  const validateMobile = (v) =>
    !v ? "" : /^\d{10}$/.test(v) ? "" : "कृपया 10 अंकों का मोबाइल नंबर दर्ज करें";
  const validateEmail = (v) =>
    !v
      ? ""
      : /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v)
        ? ""
        : "कृपया वैध ईमेल आईडी दर्ज करें";
  const validateAadhar = (v) =>
    !v ? "" : /^\d{12}$/.test(v) ? "" : "कृपया 12 अंकों का आधार नंबर दर्ज करें";
  const validatePinCode = (v) =>
    !v ? "" : /^\d{6}$/.test(v) ? "" : "6 digit pin code daalo";

  const updateForm = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleMobileChange = (v) => {
    const d = v.replace(/\D/g, "");
    setValidationErrors((p) => ({ ...p, mobile: validateMobile(d) }));
    updateForm("mobile", d);
  };
  const handleEmailChange = (v) => {
    setValidationErrors((p) => ({ ...p, email: validateEmail(v) }));
    updateForm("email", v);
  };
  const handleAadharChange = (v) => {
    const d = v.replace(/\D/g, "");
    setValidationErrors((p) => ({ ...p, aadhar: validateAadhar(d) }));
    updateForm("aadharNumber", d);
  };
  const handlePinCodeChange = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 6);
    setValidationErrors((p) => ({ ...p, pinCode: validatePinCode(d) }));
    updateForm("pinCode", d);
  };

  // ── Edu / Work helpers ────────────────────────────────────────────────────
  const calculatePercentage = (obtained, total) => {
    const o = parseFloat(obtained),
      t = parseFloat(total);
    if (!Number.isFinite(o) || !Number.isFinite(t) || t <= 0) return "";
    return ((o / t) * 100).toFixed(2);
  };

  const updateEduRow = (id, field, value) => {
    setEduRows((rows) =>
      rows.map((row) => {
        if (row.id !== id) return row;
        let updated = { ...row, [field]: value };
        if (field === "scoreType") updated.percentage = "";
        if (field === "totalMarks" || field === "obtainedMarks") {
          const st = updated.scoreType || "Percentage";
          if (st === "Percentage")
            updated.percentage = calculatePercentage(
              field === "obtainedMarks" ? value : updated.obtainedMarks,
              field === "totalMarks" ? value : updated.totalMarks,
            );
          else
            updated.percentage =
              field === "obtainedMarks" ? value : updated.obtainedMarks;
        }
        return updated;
      }),
    );
  };

  const addEduRow = () =>
    setEduRows((rows) => [
      ...rows,
      {
        id: Date.now(),
        exam: "",
        customExam: "",
        board: "",
        isBoardChecked: false,
        totalMarks: "",
        obtainedMarks: "",
        percentage: "",
        scoreType: "Percentage",
      },
    ]);

  const deleteEduRow = (id) =>
    setEduRows((rows) => rows.filter((r) => r.id !== id));

  const updateWorkRow = (id, field, value) =>
    setWorkRows((rows) =>
      rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );

  const addWorkRow = () =>
    setWorkRows((rows) => [
      ...rows,
      { id: rows.length + 1, orgName: "", duration: "", post: "", salary: "" },
    ]);

  // ── Save candidate ────────────────────────────────────────────────────────
  const handleSave = async () => {
    const mErr = validateMobile(form.mobile),
      eErr = validateEmail(form.email),
      aErr = validateAadhar(form.aadharNumber),
      pErr = validatePinCode(form.pinCode);
    setValidationErrors({
      mobile: mErr,
      email: eErr,
      aadhar: aErr,
      pinCode: pErr,
    });
    if (mErr || eErr || aErr || pErr) {
      setSaveError("❌ कृपया सभी validation errors ठीक करें");
      return;
    }
    const folder = personData?.folder;
    if (!folder) {
      setSaveError("Folder not found");
      return;
    }
    setSaving(true);
    setSaveError("");
    setSaveSuccess("");
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/update-candidate/${folder}`,
        { form, eduRows, workRows },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`
          }
        }
      );
      if (res.data.success) {
        setSaveSuccess("✅ Candidate successfully updated!");
        setEditMode(false);
        // Refresh the current person data in allPersons
        const updatedPerson = { ...personData, ...form, edu_rows: eduRows, work_rows: workRows };
        const updatedPersons = [...allPersons];
        updatedPersons[currentIndex] = updatedPerson;
        setAllPersons(updatedPersons);
        setPersonData(updatedPerson);
        setTimeout(() => setSaveSuccess(""), 4000);
      } else {
        setSaveError("❌ Update failed: " + res.data.message);
      }
    } catch (err) {
      setSaveError(
        "❌ Error: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setSaveError("");
    setSaveSuccess("");
    if (personData) initFormFromData(personData);
    setValidationErrors({ mobile: "", email: "", aadhar: "", pinCode: "" });
  };

  // ── Upload cropped photo / signature ──────────────────────────────────────
  const uploadCroppedImage = async (type) => {
    if (!completedCrop || !completedCrop.width || !completedCrop.height) {
      alert("पहले PDF पर कोई क्षेत्र खींचें (drag करके select करें)");
      return;
    }
    const folderName = personData?.folder;
    if (!folderName) {
      alert("Folder not found");
      return;
    }
    const pdfCanvas = pdfWrapperRef.current?.querySelector("canvas");
    if (!pdfCanvas) {
      alert("PDF canvas नहीं मिला। PDF लोड होने तक प्रतीक्षा करें।");
      return;
    }

    const cropped = cropPdfCanvas(
      pdfCanvas,
      completedCrop,
      pdfPageSize.width,
      pdfPageSize.height,
    );
    const base64 = cropped.toDataURL("image/jpeg", 0.95);
    setCropPreview({ base64, type });
    setPhotoSignUploading(type);

    try {
      const payload = {
        folderName,
        photo: type === "photo" ? base64 : null,
        signature: type === "signature" ? base64 : null,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_ADMIN_API_URL}/upload-photo-signature`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`
          }
        }
      );
      if (res.data.success) {
        if (type === "photo" && res.data.photoUrl)
          setSavedPhoto(res.data.photoUrl);
        if (type === "signature" && res.data.signatureUrl)
          setSavedSignature(res.data.signatureUrl);
        setSaveSuccess(
          `✅ ${type === "photo" ? "फोटो" : "हस्ताक्षर"} सफलतापूर्वक सहेजा गया!`,
        );
        setTimeout(() => setSaveSuccess(""), 4000);
        setCrop(null);
        setCompletedCrop(null);
        setCropPreview(null);
      } else {
        setSaveError("❌ Upload failed");
        setCropPreview(null);
      }
    } catch (err) {
      console.error(err);
      setSaveError(
        "❌ Upload failed: " + (err.response?.data?.message || err.message),
      );
      setCropPreview(null);
    } finally {
      setPhotoSignUploading(false);
    }
  };

  // ── Excel download ────────────────────────────────────────────────────────
  const downloadExcel = () => {
    if (!personData) {
      alert("कोई डेटा उपलब्ध नहीं है।");
      return;
    }
    const d = (val) =>
      val !== undefined && val !== null && String(val).trim() !== ""
        ? String(val).trim()
        : "—";
    const EDU_ALIAS_MAP = {
      highschool: "highschool",
      "high school": "highschool",
      "10th": "highschool",
      "10": "highschool",
      intermediate: "intermediate",
      "12th": "intermediate",
      "12": "intermediate",
      graduation: "graduation",
      "b.sc": "graduation",
      "b.a": "graduation",
      "b.com": "graduation",
      "b.tech": "graduation",
      "b.e": "graduation",
      "post graduation": "postgraduation",
      "m.sc": "postgraduation",
      "m.a": "postgraduation",
      "m.com": "postgraduation",
      "m.tech": "postgraduation",
      "m.e": "postgraduation",
      ccc: "ccc",
      "o-level": "ccc",
    };
    const resolveKey = (row) =>
      EDU_ALIAS_MAP[
        (row.exam ?? row.l ?? row.label ?? "").toLowerCase().trim()
      ] ?? null;
    const extractEdu = (eduMap, key) => {
      const row = eduMap[key];
      if (!row) return { board: "—", marks: "—", percentage: "—" };
      const obtained = row.obtainedMarks ?? row.obtain,
        total = row.totalMarks ?? row.total,
        st = row.scoreType ?? "Percentage";
      let marks = "—";
      if (st === "CGPA") marks = obtained ?? "—";
      else if (st === "Grade") marks = obtained ? `Grade: ${obtained}` : "—";
      else {
        if (obtained && total) marks = `${obtained}/${total}`;
        else if (obtained) marks = obtained;
      }
      return { board: d(row.board), marks, percentage: row.percentage ?? "—" };
    };
    const nw = (w = {}) => ({
      organization: d(w.organization ?? w.orgName ?? w.o),
      position: d(w.post ?? w.p),
      duration: d(w.duration ?? w.d),
      salary: d(w.salary ?? w.s),
    });
    const eduRowsData = parseJSON(personData.edu_rows),
      eduMap = {},
      otherEdu = [];
    for (const row of eduRowsData) {
      const key = resolveKey(row);
      if (key && !eduMap[key]) eduMap[key] = row;
      else if (!key) otherEdu.push(row);
    }
    const hs = extractEdu(eduMap, "highschool"),
      inter = extractEdu(eduMap, "intermediate"),
      grad = extractEdu(eduMap, "graduation"),
      pg = extractEdu(eduMap, "postgraduation"),
      ccc = extractEdu(eduMap, "ccc");
    const workRowsData = parseJSON(personData.work_rows),
      work1 = nw(workRowsData[0]),
      work2 = nw(workRowsData[1]);
    let corrAddr = d(
      personData.correspondence_address ?? personData.correspondenceAddress,
    );
    if (corrAddr === "—" && personData.address) corrAddr = d(personData.address);

    const mainData = {
      "S.No": 1,
      Folder: d(personData.folder),
      Post: d(personData.post),
      Vibhag: d(personData.vibhag),
      "Name (English)": d(personData.name_english),
      "Name (Hindi)": d(personData.name_hindi),
      "Father's Name": d(personData.father_name),
      DOB: d(personData.dob),
      Gender: d(personData.gender),
      Category: d(personData.category),
      Religion: d(personData.religion),
      Caste: d(personData.caste),
      Mobile: d(personData.mobile),
      Email: d(personData.email),
      "Aadhar Number": d(personData.aadhar_number),
      "Current Address": d(personData.address),
      "Correspondence Address": corrAddr,
      "Pin Code": d(personData.pin_code),
      "High School Board": hs.board,
      "High School Marks": hs.marks,
      "High School Percentage": hs.percentage,
      "Intermediate Board": inter.board,
      "Intermediate Marks": inter.marks,
      "Intermediate Percentage": inter.percentage,
      "Graduation Board": grad.board,
      "Graduation Marks": grad.marks,
      "Graduation Percentage": grad.percentage,
      "Post Graduation Board": pg.board,
      "Post Graduation Marks": pg.marks,
      "Post Graduation Percentage": pg.percentage,
      "CCC Board": ccc.board,
      "CCC Marks": ccc.marks,
      "CCC Percentage": ccc.percentage,
      "Other Education": otherEdu.length
        ? otherEdu.map((e) => `${e.exam ?? e.l}: ${d(e.board)}`).join(" | ")
        : "—",
      "Hindi Typing": d(personData.hindi_typing),
      "English Typing": d(personData.english_typing),
      "Shorthand Speed": d(personData.shorthand_speed),
      "Work 1 Organization": work1.organization,
      "Work 1 Position": work1.position,
      "Work 1 Duration": work1.duration,
      "Work 1 Salary": work1.salary,
      "Work 2 Organization": work2.organization,
      "Work 2 Position": work2.position,
      "Work 2 Duration": work2.duration,
      "Work 2 Salary": work2.salary,
      "Payment Method": d(personData.payment_method),
      "Payment Number": d(personData.payment_number),
      "Worker Mobile": d(personData.worker_mobile),
      "Created At": personData.created_at
        ? new Date(personData.created_at).toLocaleString("en-IN")
        : "—",
      "Updated At": personData.updated_at
        ? new Date(personData.updated_at).toLocaleString("en-IN")
        : "—",
    };

    const wb = XLSX.utils.book_new(),
      ws = XLSX.utils.json_to_sheet([mainData]);
    ws["!cols"] = Object.keys(mainData).map((key) => ({
      wch: Math.min(Math.max(key.length + 2, 12), 40),
    }));
    XLSX.utils.book_append_sheet(wb, ws, "Person Info");
    XLSX.writeFile(
      wb,
      `${personData.folder || "person"}_data_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="agc-container">
        <div className="header">
          <div className="header-icon">📋</div>
          <div>
            <h1 className="title">Loading Candidates...</h1>
            <p className="subtitle">Please wait while we fetch the data</p>
          </div>
        </div>
        <div className="pdf-loading" style={{ minHeight: 400 }}>
          <div className="spinner" />
          <p>डेटा लोड हो रहा है...</p>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="agc-container">
      {/* ── Main UI wrapped with agc-no-print class (hidden during print) ── */}
      <div className="agc-no-print">
        {/* ── Header ── */}
        <div className="header">
          <div className="header-icon">📋</div>
          <div>
            <h1 className="title">
              PDF Result: {personData?.folder || "No PDF Selected"}
            </h1>
            <p className="subtitle">
              PDF preview aur candidate ki poori jankari
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              className="nav-btn"
              onClick={downloadExcel}
              disabled={!personData}
            >
              📥 Excel Download
            </button>
            {/* Print Button */}
            <button
              className="nav-btn"
              onClick={() => window.print()}
              disabled={!personData}
            >
              🖨 Print Form
            </button>
            {!editMode && personData && (
              <button
                className="nav-btn res-edit-btn"
                onClick={() => {
                  setEditMode(true);
                  setSaveError("");
                  setSaveSuccess("");
                }}
              >
                ✏️ Edit
              </button>
            )}
            {editMode && (
              <>
                <button
                  className="nav-btn res-save-btn"
                  disabled={saving}
                  onClick={handleSave}
                >
                  {saving ? (
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <span className="res-spinner" />
                      Saving...
                    </span>
                  ) : (
                    "💾 Save"
                  )}
                </button>
                <button
                  className="nav-btn res-cancel-btn"
                  disabled={saving}
                  onClick={handleCancelEdit}
                >
                  ✕ Cancel
                </button>
              </>
            )}
            <button className="nav-btn" onClick={() => navigate(-1)}>
              ← Back
            </button>
          </div>
        </div>

        {/* ── Candidate Navigation Bar ── */}
        {allPersons.length > 0 && (
          <div className="candidate-bar" style={{ marginTop: 16, marginBottom: 0 }}>
            <button
              className="nav-btn"
              onClick={handlePrevious}
              disabled={currentIndex === 0}
            >
              ◀ पिछला अभ्यर्थी
            </button>

            <div className="candidate-badge">
              <span className="candidate-label">अभ्यर्थी</span>

              {/* Dropdown Selector */}
              <select
                value={currentIndex}
                onChange={handleDropdownChange}
                style={{
                  color: "var(--text, #f1f5f9)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  maxWidth: 220,
                  outline: "none",
                  background: "white",
                }}
              >
                {allPersons.map((person, i) => (
                  <option key={i} value={i}>
                    {i + 1}. {person.folder || `Candidate-${i + 1}`}
                  </option>
                ))}
              </select>

              <span className="candidate-count">
                {currentIndex + 1} / {allPersons.length}
              </span>
            </div>

            <button
              className="nav-btn"
              onClick={handleNext}
              disabled={currentIndex === allPersons.length - 1}
            >
              अगला अभ्यर्थी ▶
            </button>
          </div>
        )}

        {saveSuccess && (
          <div className="res-banner res-banner--success">{saveSuccess}</div>
        )}
        {saveError && (
          <div className="res-banner res-banner--error">{saveError}</div>
        )}
        {error && (
          <div className="res-banner res-banner--error">{error}</div>
        )}

        {/* ── Main Content ── */}
        <div className="main">
          {/* ══════════════════════════════════════════════════════════════
              LEFT: PDF PANEL
          ══════════════════════════════════════════════════════════════ */}
          <div className="image-panel">
            <div className="panel-header">
              <span className="panel-icon">📄</span>
              <h2>PDF दस्तावेज़</h2>
            </div>

            {/* Toolbar */}
            <div className="toolbar">
              <div className="toolbar-group">
                <button
                  className="toolbar-btn"
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber((p) => p - 1)}
                >
                  <span className="btn-icon">&#9664;</span>
                  <span className="btn-label">Prev</span>
                </button>
                <span className="page-info">
                  <span className="page-current">{pageNumber}</span>
                  <span className="page-sep">/</span>
                  <span className="page-total">{numPages ?? "—"}</span>
                </span>
                <button
                  className="toolbar-btn"
                  disabled={!numPages || pageNumber >= numPages}
                  onClick={() => setPageNumber((p) => p + 1)}
                >
                  <span className="btn-label">Next</span>
                  <span className="btn-icon">&#9654;</span>
                </button>
              </div>
              <div className="toolbar-divider" />
              <div className="toolbar-group">
                <button className="toolbar-btn icon-only" onClick={zoomOut}>
                  &#8722;
                </button>
                <span className="zoom-label">{Math.round(pdfScale * 100)}%</span>
                <button className="toolbar-btn icon-only" onClick={zoomIn}>
                  &#43;
                </button>
              </div>
              <div className="toolbar-divider" />
              <div className="toolbar-group">
                <button className="toolbar-btn" onClick={fitWidth}>
                  &#8596; Width
                </button>
                <button className="toolbar-btn" onClick={fitHeight}>
                  &#8597; Height
                </button>
                <button className="toolbar-btn" onClick={calcFit}>
                  &#9974; Fit
                </button>
              </div>
            </div>

            {/* OCR status hint */}
            <div
              className="ocr-status-bar"
              style={{ margin: 0, borderRadius: 0 }}
            >
              {!completedCrop ? (
                <span className="status-hint">
                  📌 PDF पर क्षेत्र खींचें → फिर Save Photo / Save Signature दबाएं
                </span>
              ) : (
                <span className="status-hint active">
                  ✂️ क्षेत्र चुना गया — नीचे Save बटन दबाएं
                </span>
              )}
            </div>

            {/* PDF Area with ReactCrop overlay */}
            <div className="pdf-area" ref={pdfContainerRef}>
              {pdfUrl && !pdfError ? (
                <div
                  style={{ position: "relative", display: "inline-block" }}
                  ref={pdfWrapperRef}
                >
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onPdfLoadSuccess}
                    onLoadError={onPdfLoadError}
                    loading={
                      <div className="pdf-loading">
                        <div className="spinner" />
                        <p>Loading PDF…</p>
                      </div>
                    }
                    error={
                      <div className="pdf-error">Failed to load PDF.</div>
                    }
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={pdfScale}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      onRenderSuccess={onPdfPageRenderSuccess}
                    />
                  </Document>

                  {/* ReactCrop overlay */}
                  {pdfPageSize.width > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: pdfPageSize.width,
                        height: pdfPageSize.height,
                        zIndex: 10,
                      }}
                    >
                      <ReactCrop
                        crop={crop}
                        onChange={handleCropChange}
                        onComplete={(c) => setCompletedCrop(c)}
                        style={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        {/* Transparent 1×1 placeholder so ReactCrop has an image to anchor to */}
                        <img
                          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                          alt=""
                          style={{
                            width: pdfPageSize.width,
                            height: pdfPageSize.height,
                            opacity: 0,
                            display: "block",
                            userSelect: "none",
                            pointerEvents: "none",
                          }}
                          draggable={false}
                        />
                      </ReactCrop>
                    </div>
                  )}
                </div>
              ) : (
                <div className="pdf-loading">
                  <span style={{ fontSize: 40 }}>📄</span>
                  <p>{pdfError ? "PDF load नहीं हो सका" : "कोई PDF नहीं"}</p>
                </div>
              )}
            </div>

            {/* Bottom download button */}
            <div className="ocr-section">
              <button
                className="ocr-btn"
                onClick={downloadExcel}
                disabled={!personData}
                style={{ background: personData ? "var(--green)" : undefined }}
              >
                📥 Excel में डाउनलोड करें
              </button>
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              RIGHT: FORM PANEL
          ══════════════════════════════════════════════════════════════ */}
          <div className="form-panel">
            <div className="panel-header">
              <span className="panel-icon">{editMode ? "✏️" : "👤"}</span>
              <h2>{editMode ? "अभ्यर्थी संपादन" : "अभ्यर्थी की जानकारी"}</h2>
              {editMode && (
                <span className="res-edit-badge">✏️ Edit Mode Active</span>
              )}
            </div>

            {error && (
              <div
                className="form-section"
                style={{ borderColor: "var(--danger)" }}
              >
                <p style={{ color: "var(--danger)" }}>⚠️ {error}</p>
              </div>
            )}
            {!error && !personData && (
              <div className="form-section">
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 0",
                    color: "var(--text-2)",
                  }}
                >
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                  <p>इस PDF के लिए कोई डेटा नहीं मिला।</p>
                </div>
              </div>
            )}

            {/* ════════════════ VIEW MODE ════════════════ */}
            {!error && mappedData && !editMode && (
              <>
                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">1</span>मूल जानकारी
                  </div>
                  <div className="res-grid">
                    <InfoRow label="Folder" value={mappedData.folder} />
                    <InfoRow label="पद" value={mappedData.post} />
                    <InfoRow label="विभाग" value={mappedData.vibhag} />
                    <InfoRow label="नाम (English)" value={mappedData.nameEnglish} />
                    <InfoRow label="नाम (हिंदी)" value={mappedData.nameHindi} />
                    <InfoRow
                      label="पिता / पति नाम"
                      value={mappedData.fatherName}
                    />
                    <InfoRow label="जन्म तिथि" value={mappedData.dob} />
                    <InfoRow label="लिंग" value={mappedData.gender} />
                    <InfoRow label="श्रेणी" value={mappedData.category} />
                    <InfoRow label="धर्म" value={mappedData.religion} />
                    <InfoRow label="जाति" value={mappedData.caste} />
                    <InfoRow
                      label="आधार संख्या"
                      value={mappedData.aadharNumber}
                    />
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">2</span>संपर्क जानकारी
                  </div>
                  <div className="res-grid">
                    <InfoRow label="मोबाइल" value={mappedData.mobile} />
                    <InfoRow label="ईमेल" value={mappedData.email} />
                  </div>
                  <div className="res-info-row res-info-full">
                    <span className="res-label">वर्तमान पता</span>
                    <span className="res-value res-address">
                      {mappedData.address}
                    </span>
                  </div>
                  <div className="res-info-row res-info-full">
                    <span className="res-label">पत्राचार पता</span>
                    <span className="res-value res-address">
                      {mappedData.correspondenceAddress}
                    </span>
                  </div>
                  <InfoRow label="पिन कोड" value={mappedData.pinCode} />
                </div>

                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">3</span>टंकण एवं आशुलिपि की
                    दक्षता
                  </div>
                  <div className="res-grid">
                    <InfoRow
                      label="हिंदी टंकण"
                      value={mappedData.hindiTyping}
                    />
                    <InfoRow
                      label="अंग्रेजी टंकण"
                      value={mappedData.englishTyping}
                    />
                    <InfoRow
                      label="आशुलिपि गति"
                      value={mappedData.shorthandSpeed}
                    />
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">4</span>भुगतान जानकारी
                  </div>
                  <div className="res-grid">
                    <InfoRow
                      label="भुगतान विधि"
                      value={mappedData.paymentMethod}
                    />
                    <InfoRow
                      label="भुगतान संख्या"
                      value={mappedData.paymentNumber}
                    />
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">5</span>शैक्षिक योग्यता
                  </div>
                  {eduRows_view.length > 0 ? (
                    <div className="table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>क्र.</th>
                            <th>परीक्षा</th>
                            <th>बोर्ड / संस्था</th>
                            <th>पूर्णांक</th>
                            <th>प्राप्तांक</th>
                            <th>%</th>
                            <th>Score Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {eduRows_view.map((row, i) => (
                            <tr key={i}>
                              <td className="row-num">{i + 1}. </td>
                              <td className="exam-label">{row.exam} </td>
                              <td>
                                {row.isBoardChecked && (
                                  <span
                                    style={{
                                      color: "var(--green)",
                                      marginRight: 4,
                                    }}
                                  >
                                    ✓
                                  </span>
                                )}
                                {row.board}
                              </td>
                              <td>{row.totalMarks}</td>
                              <td>{row.obtainedMarks}</td>
                              <td>
                                {row.percentage !== "—" ? (
                                  <span className="res-percent">
                                    {row.percentage}%
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td>{row.scoreType}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="res-empty-text">
                      कोई शैक्षिक डेटा उपलब्ध नहीं
                    </p>
                  )}
                </div>

                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">6</span>कार्य अनुभव
                  </div>
                  {workRows_view.length > 0 ? (
                    <div className="table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>क्र.</th>
                            <th>संस्था का नाम एवं पता</th>
                            <th>अवधि</th>
                            <th>पद</th>
                            <th>वेतन</th>
                          </tr>
                        </thead>
                        <tbody>
                          {workRows_view.map((row, i) => (
                            <tr key={i}>
                              <td className="row-num">
                                {String(i + 1).padStart(2, "0")}
                              </td>
                              <td
                                style={{
                                  whiteSpace: "pre-wrap",
                                  minWidth: 160,
                                }}
                              >
                                {row.organization}
                              </td>
                              <td>{row.duration}</td>
                              <td>{row.post}</td>
                              <td>{row.salary}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="res-empty-text">
                      कोई कार्य अनुभव डेटा उपलब्ध नहीं
                    </p>
                  )}
                </div>

                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">7</span>Meta जानकारी
                  </div>
                  <div className="res-grid">
                    <InfoRow
                      label="Worker Mobile"
                      value={mappedData.workerMobile}
                    />
                    <InfoRow label="Created At" value={mappedData.createdAt} />
                    <InfoRow label="Updated At" value={mappedData.updatedAt} />
                  </div>
                </div>

                <button className="save-btn" onClick={downloadExcel}>
                  📥 Excel में डाउनलोड करें
                </button>
              </>
            )}

            {/* ════════════════ EDIT MODE ════════════════ */}
            {!error && personData && editMode && (
              <>
                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">1</span>मूल जानकारी
                  </div>
                  <EditSelect
                    label="पद का नाम"
                    field="post"
                    options={POST_OPTIONS}
                    emptyLabel="पद का नाम चुनें"
                    form={form}
                    onChange={updateForm}
                  />
                  <EditSelect
                    label="विभाग"
                    field="vibhag"
                    options={VIBHAG_OPTIONS}
                    emptyLabel="विभाग चुनें"
                    form={form}
                    onChange={updateForm}
                  />
                  <div className="two-col">
                    <EditInput
                      label="नाम (English)"
                      field="nameEnglish"
                      placeholder="Full Name in English"
                      form={form}
                      onChange={updateForm}
                    />
                    <EditInput
                      label="नाम (हिंदी)"
                      field="nameHindi"
                      placeholder="पूरा नाम हिंदी में"
                      form={form}
                      onChange={updateForm}
                    />
                  </div>
                  <div className="two-col">
                    <EditInput
                      label="पिता / पति का नाम"
                      field="fatherName"
                      placeholder="पिता / पति का नाम"
                      form={form}
                      onChange={updateForm}
                    />
                    <EditInput
                      label="जन्म तिथि"
                      field="dob"
                      type="date"
                      form={form}
                      onChange={updateForm}
                    />
                  </div>
                  <div className="field-group">
                    <label>लिंग</label>
                    <div className="radio-group">
                      {[
                        ["male", "पुरुष"],
                        ["female", "महिला"],
                        ["other", "अन्य"],
                      ].map(([val, lbl]) => (
                        <label
                          key={val}
                          className={`radio-option ${form.gender === val ? "radio-active" : ""}`}
                        >
                          <input
                            type="radio"
                            name="gender"
                            value={val}
                            checked={form.gender === val}
                            onChange={(e) =>
                              updateForm("gender", e.target.value)
                            }
                          />
                          {lbl}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="two-col">
                    <div className="field-group">
                      <label>श्रेणी</label>
                      <select
                        className="field-input"
                        value={form.category || ""}
                        onChange={(e) => updateForm("category", e.target.value)}
                      >
                        <option value="">श्रेणी चुनें</option>
                        {["General", "EWS", "OBC", "SC", "ST"].map((o) => (
                          <option key={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                    <div className="field-group">
                      <label>धर्म</label>
                      <input
                        list="religion-options"
                        className="field-input"
                        placeholder="धर्म चुनें या लिखें"
                        value={form.religion || ""}
                        onChange={(e) => updateForm("religion", e.target.value)}
                      />
                      <datalist id="religion-options">
                        {[
                          "Hindu",
                          "Muslim",
                          "Sikh",
                          "Christian",
                          "Jain",
                          "Buddhist",
                          "Other",
                        ].map((o) => (
                          <option key={o} value={o} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                  <div className="two-col">
                    <EditInput
                      label="जाति"
                      field="caste"
                      placeholder="जाति"
                      form={form}
                      onChange={updateForm}
                    />
                    <div className="field-group">
                      <label>आधार संख्या</label>
                      <input
                        className={`field-input ${validationErrors.aadhar ? "error-field" : ""}`}
                        placeholder="आधार संख्या (12 अंक)"
                        value={form.aadharNumber || ""}
                        onChange={(e) => handleAadharChange(e.target.value)}
                        maxLength={12}
                      />
                      {validationErrors.aadhar && (
                        <small className="res-field-error">
                          ⚠️ {validationErrors.aadhar}
                        </small>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">2</span>संपर्क जानकारी
                  </div>
                  <div className="two-col">
                    <div className="field-group">
                      <label>मोबाइल नंबर</label>
                      <input
                        className={`field-input ${validationErrors.mobile ? "error-field" : ""}`}
                        placeholder="मोबाइल नंबर (10 अंक)"
                        value={form.mobile || ""}
                        onChange={(e) => handleMobileChange(e.target.value)}
                        maxLength={10}
                      />
                      {validationErrors.mobile && (
                        <small className="res-field-error">
                          ⚠️ {validationErrors.mobile}
                        </small>
                      )}
                    </div>
                    <div className="field-group">
                      <label>ई-मेल आईडी</label>
                      <input
                        className={`field-input ${validationErrors.email ? "error-field" : ""}`}
                        placeholder="name@example.com"
                        value={form.email || ""}
                        onChange={(e) => handleEmailChange(e.target.value)}
                      />
                      {validationErrors.email && (
                        <small className="res-field-error">
                          ⚠️ {validationErrors.email}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="field-group">
                    <label>वर्तमान पता</label>
                    <textarea
                      className="field-input field-textarea"
                      placeholder="वर्तमान पता"
                      value={form.address || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm((prev) => ({
                          ...prev,
                          address: v,
                          correspondenceAddress: prev.sameAddress
                            ? v
                            : prev.correspondenceAddress,
                        }));
                      }}
                    />
                  </div>
                  <div className="field-group">
                    <label className="res-checkbox-label">
                      <input
                        type="checkbox"
                        checked={form.sameAddress || false}
                        onChange={(e) => {
                          const c = e.target.checked;
                          setForm((prev) => ({
                            ...prev,
                            sameAddress: c,
                            correspondenceAddress: c
                              ? prev.address
                              : prev.correspondenceAddress,
                          }));
                        }}
                      />
                      वर्तमान पता ही पत्राचार पता है
                    </label>
                  </div>
                  <div className="field-group">
                    <label>पत्राचार पता</label>
                    <textarea
                      className="field-input field-textarea"
                      placeholder="पत्राचार पता"
                      value={form.correspondenceAddress || ""}
                      disabled={form.sameAddress}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          correspondenceAddress: e.target.value,
                          sameAddress: false,
                        }))
                      }
                    />
                  </div>
                  <div className="field-group">
                    <label>पिन कोड</label>
                    <input
                      className={`field-input ${validationErrors.pinCode ? "error-field" : ""}`}
                      placeholder="पिन कोड (6 अंक)"
                      value={form.pinCode || ""}
                      onChange={(e) => handlePinCodeChange(e.target.value)}
                      maxLength={6}
                    />
                    {validationErrors.pinCode && (
                      <small className="res-field-error">
                        ⚠️ {validationErrors.pinCode}
                      </small>
                    )}
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">3</span>टंकण एवं आशुलिपि की
                    दक्षता
                  </div>
                  <div className="two-col">
                    <EditInput
                      label="हिंदी टंकण गति"
                      field="hindiTyping"
                      placeholder="जैसे: 25 w/m"
                      form={form}
                      onChange={updateForm}
                    />
                    <EditInput
                      label="अंग्रेजी टंकण गति"
                      field="englishTyping"
                      placeholder="जैसे: 40 w/m"
                      form={form}
                      onChange={updateForm}
                    />
                  </div>
                  <EditInput
                    label="आशुलिपि गति"
                    field="shorthandSpeed"
                    placeholder="जैसे: 80 w/m"
                    form={form}
                    onChange={updateForm}
                  />
                </div>

                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">4</span>भुगतान जानकारी
                  </div>
                  <div className="two-col">
                    <div className="field-group">
                      <label>भुगतान विधि</label>
                      <select
                        className="field-input"
                        value={form.paymentMethod || ""}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            paymentMethod: e.target.value,
                            paymentNumber: "",
                          }))
                        }
                      >
                        <option value="">भुगतान विधि चुनें</option>
                        {PAYMENT_OPTIONS.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="field-group">
                      <label>भुगतान संख्या</label>
                      <input
                        className="field-input"
                        value={form.paymentNumber || ""}
                        disabled={!form.paymentMethod}
                        placeholder="भुगतान संख्या दर्ज करें"
                        onChange={(e) =>
                          updateForm("paymentNumber", e.target.value)
                        }
                      />
                      {!form.paymentMethod && (
                        <small
                          style={{
                            color: "#666",
                            marginTop: 4,
                            display: "block",
                          }}
                        >
                          पहले भुगतान विधि चुनें
                        </small>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">5</span>शैक्षिक योग्यता
                  </div>
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>क्र.सं.</th>
                          <th>परीक्षा</th>
                          <th>बोर्ड</th>
                          <th>Score Type</th>
                          <th>पूर्णांक</th>
                          <th>प्राप्तांक</th>
                          <th>Result</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {eduRows.map((row, idx) => (
                          <tr key={row.id}>
                            <td>{idx + 1}</td>
                            <td>
                              <select
                                className="table-input"
                                value={row.exam || ""}
                                onChange={(e) =>
                                  updateEduRow(row.id, "exam", e.target.value)
                                }
                              >
                                <option value="">Select</option>
                                {[
                                  "Highschool",
                                  "Intermediate",
                                  "Graduation",
                                  "Post Graduation",
                                  "CCC",
                                  "O-Level",
                                  "Other",
                                ].map((o) => (
                                  <option key={o} value={o}>
                                    {o}
                                  </option>
                                ))}
                              </select>
                              {row.exam === "Other" && (
                                <input
                                  className="table-input"
                                  placeholder="Custom Exam"
                                  value={row.customExam || ""}
                                  onChange={(e) =>
                                    updateEduRow(
                                      row.id,
                                      "customExam",
                                      e.target.value,
                                    )
                                  }
                                />
                              )}
                            </td>
                            <td>
                              <input
                                className="table-input"
                                value={row.board || ""}
                                placeholder="Board"
                                onChange={(e) =>
                                  updateEduRow(row.id, "board", e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <select
                                className="table-input"
                                value={row.scoreType || "Percentage"}
                                onChange={(e) =>
                                  updateEduRow(
                                    row.id,
                                    "scoreType",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="Percentage">Percentage</option>
                                <option value="CGPA">CGPA</option>
                                <option value="Grade">Grade</option>
                              </select>
                            </td>
                            <td>
                              <input
                                className="table-input small"
                                value={row.totalMarks || ""}
                                placeholder="Total"
                                onChange={(e) =>
                                  updateEduRow(
                                    row.id,
                                    "totalMarks",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="table-input small"
                                value={row.obtainedMarks || ""}
                                placeholder={
                                  row.scoreType === "CGPA"
                                    ? "CGPA"
                                    : row.scoreType === "Grade"
                                      ? "Grade"
                                      : "Marks"
                                }
                                onChange={(e) =>
                                  updateEduRow(
                                    row.id,
                                    "obtainedMarks",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="table-input small"
                                value={row.percentage || ""}
                                readOnly
                                placeholder={
                                  row.scoreType === "CGPA"
                                    ? "CGPA"
                                    : row.scoreType === "Grade"
                                      ? "Grade"
                                      : "%"
                                }
                              />
                            </td>
                            <td>
                              <button
                                className="res-delete-btn"
                                onClick={() => deleteEduRow(row.id)}
                                title="Delete"
                              >
                                🗑
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button className="add-row-btn" onClick={addEduRow}>
                    + Add Qualification
                  </button>
                </div>

                <div className="form-section">
                  <div className="section-title">
                    <span className="section-num">6</span>कार्य अनुभव का विवरण
                  </div>
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>क्र.सं.</th>
                          <th>संस्था का नाम एवं पता</th>
                          <th>अवधि</th>
                          <th>पद</th>
                          <th>वेतनमान</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workRows.map((row) => (
                          <tr key={row.id}>
                            <td className="row-num">
                              {String(row.id).padStart(2, "0")}
                            </td>
                            <td>
                              <textarea
                                className="table-input table-textarea"
                                value={row.orgName || ""}
                                placeholder="संस्था का नाम एवं पता"
                                onChange={(e) =>
                                  updateWorkRow(row.id, "orgName", e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="table-input"
                                value={row.duration || ""}
                                placeholder="01/2020 से 03/2023"
                                onChange={(e) =>
                                  updateWorkRow(
                                    row.id,
                                    "duration",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="table-input"
                                value={row.post || ""}
                                placeholder="पद"
                                onChange={(e) =>
                                  updateWorkRow(row.id, "post", e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="table-input"
                                value={row.salary || ""}
                                placeholder="वेतन"
                                onChange={(e) =>
                                  updateWorkRow(row.id, "salary", e.target.value)
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button className="add-row-btn" onClick={addWorkRow}>
                    + पंक्ति जोड़ें
                  </button>
                </div>

                <div className="action-bar-bottom">
                  <button
                    className="action-btn update wide"
                    onClick={handleSave}
                    disabled={saving}
                    style={{ opacity: saving ? 0.7 : 1 }}
                  >
                    {saving ? (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                        }}
                      >
                        <span className="res-spinner" />
                        Saving...
                      </span>
                    ) : (
                      "💾 Save Changes"
                    )}
                  </button>
                  <button
                    className="res-cancel-bottom-btn"
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    ✕ Cancel
                  </button>
                </div>
              </>
            )}

            {/* ════════════════ PHOTO & SIGNATURE SECTION ════════════════
                Always visible when personData exists (both view & edit mode)
            ══════════════════════════════════════════════════════════════ */}
            {!error && personData && (
              <div className="form-section">
                <div className="section-title">
                  <span className="section-num">📸</span>फोटो एवं हस्ताक्षर
                  (PDF से)
                </div>

                {/* Instruction banner */}
                <div
                  style={{
                    fontSize: 13,
                    marginBottom: 14,
                    background: "linear-gradient(135deg, #ebf4ff, #e8f0fe)",
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: "1px solid #c3d9f7",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    color: "#1e3a5f",
                  }}
                >
                  <span style={{ fontSize: 16, flexShrink: 0 }}>📌</span>
                  <div>
                    <strong>निर्देश:</strong> बायीं तरफ PDF पर माउस से क्षेत्र
                    खींचें (drag करें), फिर नीचे{" "}
                    <strong>"Save Photo"</strong> या{" "}
                    <strong>"Save Signature"</strong> बटन दबाएं।
                  </div>
                </div>

                {/* Upload-in-progress preview */}
                {cropPreview && (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: "12px 14px",
                      background: "#f0fdf4",
                      border: "1px solid #86efac",
                      borderRadius: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                    }}
                  >
                    <img
                      src={cropPreview.base64}
                      alt="crop preview"
                      style={{
                        height: 80,
                        maxWidth: 200,
                        objectFit: "contain",
                        border: "2px solid #4ade80",
                        borderRadius: 6,
                        background: "#fff",
                      }}
                    />
                    <div>
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#15803d",
                          fontSize: 14,
                          marginBottom: 4,
                        }}
                      >
                        {cropPreview.type === "photo"
                          ? "📸 फोटो Preview"
                          : "✍️ हस्ताक्षर Preview"}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#166534",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: 12,
                            height: 12,
                            border: "2px solid #16a34a",
                            borderTopColor: "transparent",
                            borderRadius: "50%",
                            animation: "ocr-spin 0.7s linear infinite",
                          }}
                        />
                        Upload हो रहा है…
                      </div>
                    </div>
                  </div>
                )}

                {/* Save buttons */}
                <div className="two-col" style={{ marginBottom: 16 }}>
                  <button
                    onClick={() => uploadCroppedImage("photo")}
                    disabled={!!photoSignUploading}
                    style={{
                      padding: "11px 16px",
                      borderRadius: 8,
                      border: "none",
                      cursor: photoSignUploading ? "not-allowed" : "pointer",
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#fff",
                      background:
                        photoSignUploading === "photo"
                          ? "#9ca3af"
                          : "linear-gradient(135deg, #1d4ed8, #1e40af)",
                      boxShadow:
                        photoSignUploading === "photo"
                          ? "none"
                          : "0 2px 8px rgba(29,78,216,0.35)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      transition: "all 0.2s ease",
                      width: "100%",
                      opacity: photoSignUploading ? 0.75 : 1,
                    }}
                  >
                    {photoSignUploading === "photo" ? (
                      <>
                        <span
                          style={{
                            display: "inline-block",
                            width: 14,
                            height: 14,
                            border: "2px solid rgba(255,255,255,0.5)",
                            borderTopColor: "#fff",
                            borderRadius: "50%",
                            animation: "ocr-spin 0.7s linear infinite",
                            flexShrink: 0,
                          }}
                        />
                        सहेज रहा है…
                      </>
                    ) : (
                      <>📸 Save Photo (PDF से)</>
                    )}
                  </button>

                  <button
                    onClick={() => uploadCroppedImage("signature")}
                    disabled={!!photoSignUploading}
                    style={{
                      padding: "11px 16px",
                      borderRadius: 8,
                      border: "none",
                      cursor: photoSignUploading ? "not-allowed" : "pointer",
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#fff",
                      background:
                        photoSignUploading === "signature"
                          ? "#9ca3af"
                          : "linear-gradient(135deg, #7c3aed, #6d28d9)",
                      boxShadow:
                        photoSignUploading === "signature"
                          ? "none"
                          : "0 2px 8px rgba(124,58,237,0.35)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      transition: "all 0.2s ease",
                      width: "100%",
                      opacity: photoSignUploading ? 0.75 : 1,
                    }}
                  >
                    {photoSignUploading === "signature" ? (
                      <>
                        <span
                          style={{
                            display: "inline-block",
                            width: 14,
                            height: 14,
                            border: "2px solid rgba(255,255,255,0.5)",
                            borderTopColor: "#fff",
                            borderRadius: "50%",
                            animation: "ocr-spin 0.7s linear infinite",
                            flexShrink: 0,
                          }}
                        />
                        सहेज रहा है…
                      </>
                    ) : (
                      <>✍️ Save Signature (PDF से)</>
                    )}
                  </button>
                </div>

                <p
                  style={{
                    fontSize: 11,
                    color: "#9ca3af",
                    marginTop: 0,
                    marginBottom: 16,
                    textAlign: "center",
                  }}
                >
                  ✅ Save होने के बाद crop अपने आप reset हो जाएगा
                </p>

                {/* Saved images display */}
                {(savedPhoto || savedSignature) && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 16,
                    }}
                  >
                    {savedPhoto && (
                      <div
                        style={{
                          border: "1px solid rgba(255,255,255,0.12)",
                          borderRadius: 10,
                          padding: 12,
                          background: "rgba(29,78,216,0.06)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            marginBottom: 8,
                            color: "#93c5fd",
                          }}
                        >
                          📸 Saved Photo
                        </div>
                        <img
                          src={savedPhoto}
                          alt="Candidate Photo"
                          style={{
                            width: "100%",
                            maxHeight: 160,
                            objectFit: "contain",
                            borderRadius: 6,
                            background: "#fff",
                            border: "1px solid rgba(255,255,255,0.1)",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                    {savedSignature && (
                      <div
                        style={{
                          border: "1px solid rgba(255,255,255,0.12)",
                          borderRadius: 10,
                          padding: 12,
                          background: "rgba(124,58,237,0.06)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            marginBottom: 8,
                            color: "#c4b5fd",
                          }}
                        >
                          ✍️ Saved Signature
                        </div>
                        <img
                          src={savedSignature}
                          alt="Candidate Signature"
                          style={{
                            width: "100%",
                            maxHeight: 160,
                            objectFit: "contain",
                            borderRadius: 6,
                            background: "#fff",
                            border: "1px solid rgba(255,255,255,0.1)",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── PrintFormPage Component (Direct child of agc-container, no wrapper) ── */}
      {/* {personData && (
        <PrintFormPage 
          form={form}
          eduRows={eduRows}
          workRows={workRows}
          savedPhoto={savedPhoto}
          savedSignature={savedSignature}
          folder={personData.folder}
        />
      )} */}
    </div>
  );
};

export default Result;